import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Upload, X, ImageIcon } from "lucide-react";
import { setUser, clearAuth } from "../../store/slices/authSlice";
import settingsService from "../../api/SettingsService";
import regions from "../../data/regions";
import locations from "../../data/locations";

export default function Settings() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.trp_usr);

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    company_name: "",
    email: "",
    phone_number: "",
    region: "",
    location: "",
    nic_number: "",
    contact_person: "",
    company_logo: "",
  });

  // Logo state
  const [companyLogoPreview, setCompanyLogoPreview] = useState("");
  const [logoChanged, setLogoChanged] = useState(false);
  const [originalLogoUrl, setOriginalLogoUrl] = useState("");

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    old_password: "",
    password: "",
    password_confirmation: "",
  });

  // UI state
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: "",
    color: "gray",
  });

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setProfileForm({
        company_name: user.company_name || "",
        email: user.email || "",
        phone_number: user.phone_number || "",
        region: user.region || "",
        location: user.location || "",
        nic_number: user.nic_number || "",
        contact_person: user.contact_person || "",
        company_logo: user.company_logo || "",
      });

      // Set logo preview and original URL if company_logo exists
      if (user.company_logo) {
        setCompanyLogoPreview(user.company_logo);
        setOriginalLogoUrl(user.company_logo);
      }

      // Reset logo changed flag when user data loads
      setLogoChanged(false);

      // Set available locations based on user's region
      if (user.region && locations[user.region]) {
        setAvailableLocations(locations[user.region]);
      }
    }
  }, [user]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Update available locations when region changes
    if (name === "region") {
      const newLocations = locations[value] || [];
      setAvailableLocations(newLocations);
      // Reset location if it's not available in the new region
      if (!newLocations.includes(profileForm.location)) {
        setProfileForm((prev) => ({
          ...prev,
          location: "",
        }));
      }
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Check password strength when password field changes
    if (name === "password") {
      const strength = checkPasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    // Determine company_logo value: send base64 if changed, URL if not changed
    let companyLogoValue;
    if (logoChanged) {
      // Logo was changed - send base64 if new logo uploaded, or empty string if removed
      companyLogoValue = profileForm.company_logo || "";
    } else if (!logoChanged && originalLogoUrl) {
      // Logo was not changed, send existing URL
      companyLogoValue = originalLogoUrl;
    } else {
      // No logo
      companyLogoValue = undefined;
    }

    // Create payload similar to SignUp but without password fields
    const payload = {
      company_name: profileForm.company_name,
      region: profileForm.region,
      location: profileForm.location,
      nic_number: profileForm.nic_number,
      contact_person: profileForm.contact_person,
      email: profileForm.email,
      phone_number: profileForm.phone_number,
      company_logo: companyLogoValue,
    };

    try {
      const response = await settingsService.updateProfile(payload);

      if (response.status) {
        setMessage({ type: "success", text: response.message });
        
        // Extract user data from response structure: response.data.data.data
        // Response structure: { data: { data: { data: { user fields... } } } }
        const responseUserData = response.data?.data?.data || null;
        
        if (responseUserData) {
          // Extract company_logo URL from response (always a URL, never base64)
          const newLogoUrl = responseUserData.company_logo || "";
          
          // Update Redux store with all user data from response
          // Always use URL from response, never base64
          const updatedUser = {
            ...user,
            company_name: responseUserData.company_name || user.company_name,
            email: responseUserData.email || user.email,
            phone_number: responseUserData.phone_number || user.phone_number,
            region: responseUserData.region || user.region,
            location: responseUserData.location || user.location,
            nic_number: responseUserData.nic_number || user.nic_number,
            contact_person: responseUserData.contact_person || user.contact_person,
            company_logo: newLogoUrl,
            email_verified_at: responseUserData.email_verified_at !== undefined ? responseUserData.email_verified_at : user.email_verified_at,
            phone_number_verified_at: responseUserData.phone_number_verified_at !== undefined ? responseUserData.phone_number_verified_at : user.phone_number_verified_at,
            slug: responseUserData.slug || user.slug,
          };
          dispatch(setUser(updatedUser));
          
          // Update form state with response data
          setProfileForm({
            company_name: responseUserData.company_name || "",
            email: responseUserData.email || "",
            phone_number: responseUserData.phone_number || "",
            region: responseUserData.region || "",
            location: responseUserData.location || "",
            nic_number: responseUserData.nic_number || "",
            contact_person: responseUserData.contact_person || "",
            company_logo: newLogoUrl, // Store URL, not base64
          });
          
          // Update original logo URL and preview with URL from response
          setOriginalLogoUrl(newLogoUrl);
          setCompanyLogoPreview(newLogoUrl);
        } else {
          // Fallback: if response structure is different, update with form data and existing logo URL
          const updatedUser = {
            ...user,
            ...profileForm,
            company_logo: logoChanged ? (originalLogoUrl || "") : (originalLogoUrl || user.company_logo),
          };
          dispatch(setUser(updatedUser));
        }
        
        setLogoChanged(false);
      } else {
        setMessage({ type: "error", text: response.message });
      }
    } catch (error) {
      setMessage({ type: "error", text: "An unexpected error occurred" });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    // Validate passwords match
    if (passwordForm.password !== passwordForm.password_confirmation) {
      setMessage({ type: "error", text: "New passwords do not match" });
      setLoading(false);
      return;
    }

    // Validate password strength
    if (passwordForm.password.length < 8) {
      setMessage({
        type: "error",
        text: "Password must be at least 8 characters long",
      });
      setLoading(false);
      return;
    }

    try {
      const response = await settingsService.changePassword({
        old_password: passwordForm.old_password,
        password: passwordForm.password,
        password_confirmation: passwordForm.password_confirmation,
      });

      if (response.status) {
        setMessage({ type: "success", text: response.message });
        setPasswordForm({
          old_password: "",
          password: "",
          password_confirmation: "",
        });
        setShowPasswordForm(false);

        setTimeout(() => {
          dispatch(clearAuth());
          window.location.href = "/login";
        }, 2000);
      } else {
        setMessage({ type: "error", text: response.message });
      }
    } catch (error) {
      setMessage({ type: "error", text: "An unexpected error occurred" });
    } finally {
      setLoading(false);
    }
  };

  const checkPasswordStrength = (password) => {
    let score = 0;
    let feedback = "";
    let color = "gray";

    if (!password) {
      return { score: 0, feedback: "", color: "gray" };
    }

    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Character variety checks
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    // Determine strength level
    if (score <= 2) {
      feedback = "Weak";
      color = "red";
    } else if (score <= 4) {
      feedback = "Fair";
      color = "yellow";
    } else if (score <= 5) {
      feedback = "Good";
      color = "blue";
    } else {
      feedback = "Strong";
      color = "green";
    }

    return { score, feedback, color };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not verified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
      <div className="px-4 py-8 mx-auto max-w-6xl sm:px-6 lg:px-8">
        {/* Enhanced Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative mb-12"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-r rounded-3xl from-blue-600/10 to-indigo-600/10"></div>
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>

          <div className="relative px-6 py-8 rounded-2xl border shadow-xl backdrop-blur-sm bg-white/80 border-white/20">
            <div className="flex flex-col justify-between items-center lg:flex-row">
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h1 className="mb-3 text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-800 sm:text-4xl lg:text-5xl">
                  Account Settings
                </h1>
                <p className="max-w-2xl text-lg leading-relaxed text-gray-600">
                  Manage your TripSecure provider account settings and
                  preferences with ease.
                </p>
              </div>

              {/* User Info Card */}
              <div className="mt-6 lg:mt-0 lg:ml-6">
                <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border shadow-lg border-blue-200/50">
                  <div className="flex items-center space-x-3">
                    {user?.company_logo ? (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200, damping: 15 }}
                        className="overflow-hidden flex-shrink-0 w-12 h-12 bg-white rounded-lg ring-2 ring-blue-200 shadow-md"
                      >
                        <img
                          src={user.company_logo}
                          alt="Company Logo"
                          className="object-contain p-1.5 w-full h-full"
                        />
                      </motion.div>
                    ) : (
                      <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                    )}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        {user?.company_name || "Provider"}
                      </h3>
                      <p className="text-xs text-gray-600">{user?.email}</p>
                      <div className="flex items-center mt-1">
                        <div
                          className={`w-1.5 h-1.5 rounded-full ${
                            user?.email_verified_at
                              ? "bg-green-500"
                              : "bg-gray-400"
                          } mr-1.5`}
                        ></div>
                        <span className="text-xs text-gray-500">
                          {user?.email_verified_at ? "Verified" : "Unverified"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Message Display */}
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-8 p-4 rounded-xl border-l-4 shadow-lg ${
              message.type === "success"
                ? "bg-green-50 border-green-400 text-green-800"
                : "bg-red-50 border-red-400 text-red-800"
            }`}
          >
            <div className="flex items-center">
              <div
                className={`flex-shrink-0 w-5 h-5 rounded-full ${
                  message.type === "success" ? "bg-green-400" : "bg-red-400"
                }`}
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  {message.type === "success" ? (
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  ) : (
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  )}
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{message.text}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Tab Navigation */}
        <div className="flex p-2 mx-auto mb-8 space-x-1 max-w-md bg-white rounded-2xl border border-gray-100 shadow-lg">
          <button
            onClick={() => setActiveTab("profile")}
            className={`flex-1 py-3 px-6 rounded-xl text-sm font-semibold transition-all duration-200 ${
              activeTab === "profile"
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg transform scale-105"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <div className="flex justify-center items-center space-x-2">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span>Profile</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab("security")}
            className={`flex-1 py-3 px-6 rounded-xl text-sm font-semibold transition-all duration-200 ${
              activeTab === "security"
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg transform scale-105"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <div className="flex justify-center items-center space-x-2">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <span>Security</span>
            </div>
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-xl"
          >
            <div className="px-8 py-6 bg-gradient-to-r from-blue-600 to-indigo-600">
              <div className="flex items-center space-x-3">
                <div className="flex justify-center items-center w-10 h-10 bg-white bg-opacity-20 rounded-lg">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Profile Information
                  </h2>
                  <p className="text-blue-100">
                    Update your company and contact information.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleProfileSubmit} className="p-8 space-y-8">
              {/* Company Logo Upload Section */}
              <div className="pb-8 border-b border-gray-200">
                <label className="flex items-center mb-4 text-sm font-semibold text-gray-700">
                  <svg
                    className="mr-2 w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Company Logo
                </label>
                <LogoUploadField
                  value={profileForm.company_logo}
                  preview={companyLogoPreview}
                  onChange={(base64, preview) => {
                    setProfileForm((prev) => ({ ...prev, company_logo: base64 }));
                    setCompanyLogoPreview(preview);
                    // Mark logo as changed if base64 is provided (new upload)
                    if (base64 && base64.startsWith('data:image')) {
                      setLogoChanged(true);
                    } else if (!base64) {
                      // Logo was removed
                      setLogoChanged(true);
                      setOriginalLogoUrl("");
                    }
                  }}
                  delay={0}
                />
              </div>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="block flex items-center mb-3 text-sm font-semibold text-gray-700">
                    <svg
                      className="mr-2 w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={profileForm.company_name}
                    onChange={handleProfileChange}
                    className="px-4 py-3 w-full bg-gray-50 rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-white"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="block flex items-center mb-3 text-sm font-semibold text-gray-700">
                    <svg
                      className="mr-2 w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Contact Person
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={profileForm.contact_person}
                    onChange={handleProfileChange}
                    className="px-4 py-3 w-full bg-gray-50 rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-white"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="block flex items-center mb-3 text-sm font-semibold text-gray-700">
                    <svg
                      className="mr-2 w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Email Address
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={profileForm.email}
                      disabled
                      className="px-4 py-3 w-full text-gray-600 bg-gray-100 rounded-xl border border-gray-300 cursor-not-allowed"
                    />
                    <div className="flex absolute inset-y-0 right-0 items-center pr-3">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        user?.email_verified_at ? "bg-green-500" : "bg-gray-400"
                      }`}
                    ></div>
                    <span>
                      Email verified: {formatDate(user?.email_verified_at)}
                    </span>
                  </div>
                  <div className="flex items-center p-2 space-x-2 text-xs text-amber-600 bg-amber-50 rounded-lg border border-amber-200">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      Email cannot be changed. Contact support for assistance.
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block flex items-center mb-3 text-sm font-semibold text-gray-700">
                    <svg
                      className="mr-2 w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={profileForm.phone_number}
                    onChange={handleProfileChange}
                    className="px-4 py-3 w-full bg-gray-50 rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-white"
                    required
                  />
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        user?.phone_number_verified_at
                          ? "bg-green-500"
                          : "bg-gray-400"
                      }`}
                    ></div>
                    <span>
                      Phone verified:{" "}
                      {formatDate(user?.phone_number_verified_at)}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block flex items-center mb-3 text-sm font-semibold text-gray-700">
                    <svg
                      className="mr-2 w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Region
                  </label>
                  <div className="relative">
                    <select
                      name="region"
                      value={profileForm.region}
                      onChange={handleProfileChange}
                      className="px-4 py-3 w-full bg-gray-50 rounded-xl border border-gray-300 transition-all duration-200 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-white"
                      required
                    >
                      <option value="">Select a region</option>
                      {regions.map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </select>
                    <div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block flex items-center mb-3 text-sm font-semibold text-gray-700">
                    <svg
                      className="mr-2 w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                    Location
                  </label>
                  <div className="relative">
                    <select
                      name="location"
                      value={profileForm.location}
                      onChange={handleProfileChange}
                      className="px-4 py-3 w-full bg-gray-50 rounded-xl border border-gray-300 transition-all duration-200 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-white"
                      required
                      disabled={!profileForm.region}
                    >
                      <option value="">
                        {profileForm.region
                          ? "Select a location"
                          : "Select a region first"}
                      </option>
                      {availableLocations.map((location) => (
                        <option key={location} value={location}>
                          {location}
                        </option>
                      ))}
                    </select>
                    <div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
                      <svg
                        className="w-5 h-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                  {!profileForm.region && (
                    <div className="flex items-center p-2 space-x-2 text-xs text-blue-600 bg-blue-50 rounded-lg border border-blue-200">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>
                        Please select a region first to see available locations
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="block flex items-center mb-3 text-sm font-semibold text-gray-700">
                    <svg
                      className="mr-2 w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    NIC Number
                  </label>
                  <input
                    type="text"
                    name="nic_number"
                    value={profileForm.nic_number}
                    onChange={handleProfileChange}
                    className="px-4 py-3 w-full bg-gray-50 rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-white"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end pt-6 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg transition-all duration-200 transform hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <svg
                        className="mr-3 -ml-1 w-5 h-5 text-white animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Updating...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>Update Profile</span>
                    </div>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Security Tab */}
        {activeTab === "security" && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-xl"
          >
            <div className="px-4 py-4 bg-gradient-to-r from-green-600 to-emerald-600 sm:px-8 sm:py-6">
              <div className="flex items-center space-x-3">
                <div className="flex justify-center items-center w-8 h-8 bg-white bg-opacity-20 rounded-lg sm:w-10 sm:h-10">
                  <svg
                    className="w-4 h-4 text-white sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white sm:text-2xl">
                    Security Settings
                  </h2>
                  <p className="text-sm text-green-100 sm:text-base">
                    Manage your account security and password.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-8">
              {!showPasswordForm ? (
                <div className="space-y-6 sm:space-y-8">
                  <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200 sm:p-6">
                                         <div className="flex items-center mb-4 space-x-3 sm:space-x-4">
                       <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl sm:w-12 sm:h-12">
                         <svg
                           className="flex-shrink-0 w-5 h-5 text-white sm:w-6 sm:h-6"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                         >
                           <path
                             strokeLinecap="round"
                             strokeLinejoin="round"
                             strokeWidth={2}
                             d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                           />
                         </svg>
                       </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          Password
                        </h3>
                        <p className="text-gray-600">
                          Change your account password to keep your account
                          secure.
                        </p>
                      </div>
                    </div>
                     <button
                       onClick={() => setShowPasswordForm(true)}
                       className="px-4 py-3 w-full text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl shadow-lg transition-all duration-200 transform hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 hover:scale-105 sm:w-auto sm:px-6"
                     >
                       <div className="flex justify-center items-center space-x-3 sm:justify-start sm:space-x-2">
                         <svg
                           className="flex-shrink-0 w-5 h-5"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                         >
                           <path
                             strokeLinecap="round"
                             strokeLinejoin="round"
                             strokeWidth={2}
                             d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                           />
                         </svg>
                         <span className="text-sm sm:text-base">Change Password</span>
                       </div>
                     </button>
                  </div>

                  <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200 sm:p-6">
                                         <div className="flex items-center mb-4 space-x-3 sm:space-x-4">
                       <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl sm:w-12 sm:h-12">
                         <svg
                           className="flex-shrink-0 w-5 h-5 text-white sm:w-6 sm:h-6"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                         >
                           <path
                             strokeLinecap="round"
                             strokeLinejoin="round"
                             strokeWidth={2}
                             d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                           />
                         </svg>
                       </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900">
                          Account Information
                        </h3>
                        <p className="text-gray-600">
                          Your account details and verification status.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-3 text-sm sm:gap-4 sm:grid-cols-2">
                      <div className="p-3 bg-white rounded-lg border border-gray-200 sm:p-4">
                        <p className="text-xs font-medium text-gray-500 sm:text-sm">Account ID</p>
                        <p className="text-sm font-semibold text-gray-900 sm:text-base">
                          {user?.id}
                        </p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-gray-200 sm:p-4">
                        <p className="text-xs font-medium text-gray-500 sm:text-sm">Created</p>
                        <p className="text-sm font-semibold text-gray-900 sm:text-base">
                          {formatDate(user?.created_at)}
                        </p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-gray-200 sm:p-4">
                        <p className="text-xs font-medium text-gray-500 sm:text-sm">
                          Last Updated
                        </p>
                        <p className="text-sm font-semibold text-gray-900 sm:text-base">
                          {formatDate(user?.updated_at)}
                        </p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-gray-200 sm:p-4">
                        <p className="text-xs font-medium text-gray-500 sm:text-sm">
                          Account Slug
                        </p>
                        <p className="font-mono text-xs font-semibold text-gray-900 break-all">
                          {user?.slug}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handlePasswordSubmit} className="space-y-4 sm:space-y-6">
                  <div className="space-y-2">
                    <label className="block flex items-center mb-2 text-xs font-semibold text-gray-700 sm:mb-3 sm:text-sm">
                      <svg
                        className="mr-2 w-3 h-3 text-gray-500 sm:w-4 sm:h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                      Current Password
                    </label>
                    <input
                      type="password"
                      name="old_password"
                      value={passwordForm.old_password}
                      onChange={handlePasswordChange}
                      className="px-3 py-2.5 w-full bg-gray-50 rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-white sm:px-4 sm:py-3"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block flex items-center mb-2 text-xs font-semibold text-gray-700 sm:mb-3 sm:text-sm">
                      <svg
                        className="mr-2 w-3 h-3 text-gray-500 sm:w-4 sm:h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                      New Password
                    </label>
                                         <input
                       type="password"
                       name="password"
                       value={passwordForm.password}
                       onChange={handlePasswordChange}
                       className="px-3 py-2.5 w-full bg-gray-50 rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-white sm:px-4 sm:py-3"
                       required
                     />
                     
                     {/* Password Strength Indicator */}
                     {passwordForm.password && (
                       <div className="space-y-2">
                         <div className="flex justify-between items-center">
                           <span className="text-xs font-medium text-gray-600">Password Strength:</span>
                           <span className={`text-xs font-semibold ${
                             passwordStrength.color === "red" ? "text-red-600" :
                             passwordStrength.color === "yellow" ? "text-yellow-600" :
                             passwordStrength.color === "blue" ? "text-blue-600" :
                             passwordStrength.color === "green" ? "text-green-600" : "text-gray-600"
                           }`}>
                             {passwordStrength.feedback}
                           </span>
                         </div>
                         <div className="w-full h-2 bg-gray-200 rounded-full">
                           <div
                             className={`h-2 rounded-full transition-all duration-300 ${
                               passwordStrength.color === "red" ? "bg-red-500" :
                               passwordStrength.color === "yellow" ? "bg-yellow-500" :
                               passwordStrength.color === "blue" ? "bg-blue-500" :
                               passwordStrength.color === "green" ? "bg-green-500" : "bg-gray-400"
                             }`}
                             style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                           ></div>
                         </div>
                         <div className="flex flex-wrap gap-1">
                           <div className={`text-xs px-2 py-1 rounded ${
                             passwordForm.password.length >= 8 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                           }`}>
                             ✓ 8+ chars
                           </div>
                           <div className={`text-xs px-2 py-1 rounded ${
                             /[a-z]/.test(passwordForm.password) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                           }`}>
                             ✓ Lowercase
                           </div>
                           <div className={`text-xs px-2 py-1 rounded ${
                             /[A-Z]/.test(passwordForm.password) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                           }`}>
                             ✓ Uppercase
                           </div>
                           <div className={`text-xs px-2 py-1 rounded ${
                             /[0-9]/.test(passwordForm.password) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                           }`}>
                             ✓ Number
                           </div>
                           <div className={`text-xs px-2 py-1 rounded ${
                             /[^A-Za-z0-9]/.test(passwordForm.password) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                           }`}>
                             ✓ Special
                           </div>
                         </div>
                       </div>
                     )}
                     
                     <div className="flex items-center space-x-2 text-xs text-gray-500">
                       <svg
                         className="w-3 h-3 sm:w-4 sm:h-4"
                         fill="none"
                         stroke="currentColor"
                         viewBox="0 0 24 24"
                       >
                         <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                         />
                       </svg>
                       <span className="text-xs sm:text-sm">Password must be at least 8 characters long</span>
                     </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block flex items-center mb-2 text-xs font-semibold text-gray-700 sm:mb-3 sm:text-sm">
                      <svg
                        className="mr-2 w-3 h-3 text-gray-500 sm:w-4 sm:h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      name="password_confirmation"
                      value={passwordForm.password_confirmation}
                      onChange={handlePasswordChange}
                      className="px-3 py-2.5 w-full bg-gray-50 rounded-xl border border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:bg-white sm:px-4 sm:py-3"
                      required
                    />
                  </div>

                  <div className="flex flex-col pt-6 space-y-3 border-t border-gray-100 sm:flex-row sm:space-y-0 sm:space-x-4">
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-3 w-full text-white bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl shadow-lg transition-all duration-200 transform hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 sm:w-auto sm:px-8"
                    >
                      {loading ? (
                        <div className="flex items-center space-x-2">
                          <svg
                            className="mr-3 -ml-1 w-5 h-5 text-white animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>Updating...</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span>Update Password</span>
                        </div>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordForm({
                          old_password: "",
                          password: "",
                          password_confirmation: "",
                        });
                      }}
                      className="px-6 py-3 w-full text-gray-700 bg-gray-300 rounded-xl transition-all duration-200 transform hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 hover:scale-105 sm:w-auto sm:px-8"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

// Logo Upload Field Component with Drag & Drop
function LogoUploadField({ value, preview, onChange, delay = 0, error, validationError }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const validateFile = (file) => {
    // Check file type
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setUploadError("Please upload a valid image file (JPG, PNG, GIF, or WebP)");
      return false;
    }

    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setUploadError("File size must be less than 5MB");
      return false;
    }

    setUploadError("");
    return true;
  };

  const handleFile = async (file) => {
    if (!validateFile(file)) return;

    try {
      const base64 = await convertToBase64(file);
      onChange(base64, URL.createObjectURL(file));
    } catch (error) {
      setUploadError("Failed to process image. Please try again.");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleRemove = () => {
    onChange("", "");
    setUploadError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Check if we have an existing logo (base64 string) or preview URL
  const hasLogo = preview || value;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="relative group"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        className="hidden"
      />

      <AnimatePresence mode="wait">
        {!hasLogo ? (
          <motion.div
            key="upload"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
            className={`relative overflow-hidden rounded-xl border-2 border-dashed transition-all duration-300 cursor-pointer ${
              isDragging
                ? "border-blue-500 bg-blue-50 scale-[1.02]"
                : error || validationError || uploadError
                ? "border-red-300 bg-red-50 hover:border-red-400"
                : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50"
            }`}
          >
            <motion.div
              className="flex flex-col justify-center items-center px-4 py-8"
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <motion.div
                animate={
                  isDragging
                    ? { scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }
                    : {}
                }
                transition={{ duration: 0.5 }}
                className={`relative mb-4 p-4 rounded-full ${
                  isDragging
                    ? "bg-blue-100"
                    : error || validationError || uploadError
                    ? "bg-red-100"
                    : "bg-gray-100 group-hover:bg-blue-100"
                }`}
              >
                <motion.div
                  animate={isDragging ? { y: [-2, 2, -2] } : {}}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <Upload
                    size={32}
                    className={`${
                      isDragging
                        ? "text-blue-600"
                        : error || validationError || uploadError
                        ? "text-red-500"
                        : "text-gray-400 group-hover:text-blue-600"
                    } transition-colors duration-300`}
                  />
                </motion.div>

                {/* Animated particles when dragging */}
                {isDragging && (
                  <>
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-2 h-2 bg-blue-400 rounded-full"
                        initial={{
                          x: 0,
                          y: 0,
                          opacity: 1,
                        }}
                        animate={{
                          x: Math.cos((i * Math.PI) / 3) * 40,
                          y: Math.sin((i * Math.PI) / 3) * 40,
                          opacity: 0,
                        }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          delay: i * 0.1,
                        }}
                        style={{
                          left: "50%",
                          top: "50%",
                        }}
                      />
                    ))}
                  </>
                )}
              </motion.div>

              <motion.h4
                className={`text-sm font-semibold mb-1 ${
                  isDragging
                    ? "text-blue-600"
                    : error || validationError || uploadError
                    ? "text-red-600"
                    : "text-gray-700"
                }`}
              >
                {isDragging
                  ? "Drop your logo here"
                  : "Upload Company Logo"}
              </motion.h4>

              <p className="mb-2 text-xs text-center text-gray-500">
                Drag and drop or click to browse
              </p>

              <div className="flex gap-2 items-center text-xs text-gray-400">
                <ImageIcon size={14} />
                <span>JPG, PNG, GIF or WebP (max 5MB)</span>
              </div>

              {/* Animated border effect */}
              <motion.div
                className="absolute inset-0 rounded-xl"
                animate={
                  isDragging
                    ? {
                        boxShadow: [
                          "0 0 0 0 rgba(37, 99, 235, 0.4)",
                          "0 0 0 10px rgba(37, 99, 235, 0)",
                        ],
                      }
                    : {}
                }
                transition={{ duration: 1, repeat: Infinity }}
              />
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="preview"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="overflow-hidden relative bg-gradient-to-br from-blue-50 to-white rounded-xl border-2 border-blue-200"
          >
            <div className="flex gap-4 items-center p-4">
              {/* Logo Preview */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="overflow-hidden relative flex-shrink-0 w-24 h-24 bg-white rounded-lg ring-2 ring-blue-100 shadow-lg"
              >
                <img
                  src={preview || value}
                  alt="Company Logo"
                  className="object-contain p-2 w-full h-full"
                />
                
                {/* Success checkmark animation */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="flex absolute top-1 right-1 justify-center items-center w-6 h-6 bg-blue-500 rounded-full shadow-lg"
                >
                  <motion.svg
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <motion.path
                      d="M2 7L5.5 10.5L12 4"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </motion.div>
              </motion.div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex gap-2 items-center mb-1"
                >
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  <h4 className="text-sm font-semibold text-gray-900">
                    {value && !preview ? "Current Company Logo" : "Logo Ready"}
                  </h4>
                </motion.div>
                
                <motion.p
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mb-2 text-xs text-gray-500"
                >
                  {value && !preview 
                    ? "Your current company logo" 
                    : "Click to change or remove"}
                </motion.p>

                <motion.button
                  type="button"
                  onClick={handleClick}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex gap-1 items-center text-xs font-medium text-blue-600 transition-colors hover:text-blue-700"
                >
                  <Upload size={12} />
                  {value && !preview ? "Change Logo" : "Update Logo"}
                </motion.button>
              </div>

              {/* Remove Button */}
              <motion.button
                type="button"
                onClick={handleRemove}
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.3, type: "spring" }}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                className="flex-shrink-0 p-2 text-red-500 bg-red-50 rounded-lg transition-colors hover:bg-red-100"
              >
                <X size={18} />
              </motion.button>
            </div>

            {/* Animated gradient border */}
            <motion.div
              className="absolute inset-0 rounded-xl pointer-events-none"
              animate={{
                background: [
                  "linear-gradient(0deg, rgba(37, 99, 235, 0.1) 0%, transparent 100%)",
                  "linear-gradient(360deg, rgba(37, 99, 235, 0.1) 0%, transparent 100%)",
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Messages */}
      <AnimatePresence>
        {(error || validationError || uploadError) && (
          <motion.div
            initial={{ opacity: 0, y: -5, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -5, height: 0 }}
            className="overflow-hidden"
          >
            <motion.p
              initial={{ x: -10 }}
              animate={{ x: 0 }}
              className="flex gap-1 items-center mt-2 text-xs text-red-600"
            >
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.3 }}
              >
                ⚠️
              </motion.span>
              {error || validationError || uploadError}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
